import type {
  DiagnosisContext,
  DiagnosisEvidence,
  ScoreComponent,
  ScoreConfig,
  ScoreDimension,
  ScoreDimensionResult,
  ScoreResult,
} from './contracts';

const clamp = (value: number): number => Math.max(0, Math.min(100, value));
const metricValue = (metric: { status: string; effectiveValue: unknown }): number | null =>
  metric.status === 'known' && typeof metric.effectiveValue === 'number'
    ? metric.effectiveValue
    : null;

function evidence(
  metric: string,
  value: number,
  explanation: string,
  nodeId?: string,
): DiagnosisEvidence {
  return { metric, value, ...(nodeId ? { nodeId } : {}), source: 'calculated', explanation };
}

function component(
  dimension: ScoreDimension,
  id: string,
  value: number | null,
  details: DiagnosisEvidence[],
  explanation: string,
): ScoreComponent {
  if (value === null) {
    return { id, dimension, valueState: 'unknown', weightKey: id, evidence: [], explanation };
  }
  const normalizedValue = clamp(value);
  return {
    id,
    dimension,
    valueState: 'known',
    normalizedValue,
    weightKey: id,
    evidence: details,
    positiveContribution: normalizedValue >= 50 ? normalizedValue : undefined,
    negativeContribution: normalizedValue < 50 ? 100 - normalizedValue : undefined,
    explanation,
  };
}

function acquisitionComponents(context: DiagnosisContext): ScoreComponent[] {
  const nodes = context.graph.nodes.filter((node) => node.category === 'acquisition');
  const sources = new Set(nodes.map((node) => node.type));
  const volume = nodes
    .map((node) => metricValue(node.metrics.entries))
    .filter((value): value is number => value !== null);
  const conversions = nodes
    .map((node) => metricValue(node.metrics.conversion))
    .filter((value): value is number => value !== null);
  return [
    component(
      'acquisition',
      'source_diversity',
      nodes.length ? Math.min(100, sources.size * 34) : null,
      nodes.length
        ? [evidence('source_count', sources.size, 'Distinct acquisition source types represented.')]
        : [],
      'Rewards explicit source diversity without assuming a missing source is failure.',
    ),
    component(
      'acquisition',
      'investment_clarity',
      metricValue(context.funnelMetrics.mediaInvestment) === null ? null : 100,
      metricValue(context.funnelMetrics.mediaInvestment) === null
        ? []
        : [
            evidence(
              'mediaInvestment',
              metricValue(context.funnelMetrics.mediaInvestment)!,
              'Media investment has a known basis.',
            ),
          ],
      'Known investment basis improves acquisition explainability.',
    ),
    component(
      'acquisition',
      'volume',
      volume.length
        ? Math.min(
            100,
            50 +
              Math.log10(
                Math.max(
                  1,
                  volume.reduce((sum, value) => sum + value, 0),
                ),
              ) *
                10,
          )
        : null,
      volume.length
        ? [
            evidence(
              'entries',
              volume.reduce((sum, value) => sum + value, 0),
              'Known acquisition volume.',
            ),
          ]
        : [],
      'Volume is scored only when explicitly known.',
    ),
    component(
      'acquisition',
      'efficiency',
      conversions.length
        ? (conversions.reduce((sum, value) => sum + value, 0) / conversions.length) * 100
        : null,
      conversions.length
        ? [
            evidence(
              'conversion',
              conversions.reduce((sum, value) => sum + value, 0) / conversions.length,
              'Mean known acquisition-stage conversion.',
            ),
          ]
        : [],
      'Known rates provide an internal efficiency signal.',
    ),
  ];
}

function conversionComponents(context: DiagnosisContext): ScoreComponent[] {
  const nodes = context.graph.nodes.filter((node) => node.category === 'conversion');
  const rates = nodes
    .map((node) => metricValue(node.metrics.conversion))
    .filter((value): value is number => value !== null);
  const warned = context.graph.edges.filter((edge) => edge.warnings.length > 0).length;
  return [
    component(
      'conversion',
      'rates',
      rates.length ? (rates.reduce((sum, value) => sum + value, 0) / rates.length) * 100 : null,
      rates.length
        ? [
            evidence(
              'conversion',
              rates.reduce((sum, value) => sum + value, 0) / rates.length,
              'Mean of known conversion-stage rates.',
            ),
          ]
        : [],
      'Uses only known stage rates.',
    ),
    component(
      'conversion',
      'stage_clarity',
      nodes.length ? (rates.length / nodes.length) * 100 : null,
      nodes.length
        ? [
            evidence(
              'known_rate_coverage',
              rates.length / nodes.length,
              'Share of conversion stages with a known rate.',
            ),
          ]
        : [],
      'Measures whether conversion stages have usable inputs.',
    ),
    component(
      'conversion',
      'consistency',
      context.graph.edges.length
        ? Math.max(0, 100 - (warned / context.graph.edges.length) * 100)
        : null,
      context.graph.edges.length
        ? [
            evidence(
              'warning_ratio',
              warned / context.graph.edges.length,
              'Share of edges with metric warnings.',
            ),
          ]
        : [],
      'Metric-flow warnings reduce consistency.',
    ),
    component(
      'conversion',
      'goals',
      nodes.length
        ? (nodes.filter((node) => node.metrics.goal.status === 'known').length / nodes.length) * 100
        : null,
      nodes.length
        ? [
            evidence(
              'goal_coverage',
              nodes.filter((node) => node.metrics.goal.status === 'known').length / nodes.length,
              'Share of conversion stages with explicit goals.',
            ),
          ]
        : [],
      'Explicit goals make performance interpretable.',
    ),
  ];
}

function commercialComponents(context: DiagnosisContext): ScoreComponent[] {
  const commercial = context.graph.nodes.filter((node) => node.category === 'commercial');
  const types = new Set(context.graph.nodes.map((node) => node.type));
  const required = ['sdr-seller', 'meeting', 'proposal', 'sale'];
  const conversion = commercial
    .map((node) => metricValue(node.metrics.conversion))
    .filter((value): value is number => value !== null);
  return [
    component(
      'commercial',
      'pipeline_representation',
      commercial.length
        ? (required.filter((type) => types.has(type)).length / required.length) * 100
        : null,
      commercial.length
        ? [
            evidence(
              'represented_stages',
              required.filter((type) => types.has(type)).length,
              'Commercial milestones represented in the graph.',
            ),
          ]
        : [],
      'Measures explicit representation, not business success.',
    ),
    component(
      'commercial',
      'close_efficiency',
      conversion.length
        ? (conversion.reduce((sum, value) => sum + value, 0) / conversion.length) * 100
        : null,
      conversion.length
        ? [
            evidence(
              'conversion',
              conversion.reduce((sum, value) => sum + value, 0) / conversion.length,
              'Mean known commercial-stage conversion.',
            ),
          ]
        : [],
      'Uses known commercial conversion rates.',
    ),
    component(
      'commercial',
      'follow_up',
      commercial.length
        ? ['crm', 'email', 'whatsapp'].some((type) => types.has(type))
          ? 100
          : 30
        : null,
      commercial.length
        ? [
            evidence(
              'follow_up_representation',
              ['crm', 'email', 'whatsapp'].filter((type) => types.has(type)).length,
              'Follow-up mechanisms represented.',
            ),
          ]
        : [],
      'Checks representation only; absence is not a causal claim.',
    ),
    component(
      'commercial',
      'goals',
      commercial.length
        ? (commercial.filter((node) => node.metrics.goal.status === 'known').length /
            commercial.length) *
            100
        : null,
      commercial.length
        ? [
            evidence(
              'goal_coverage',
              commercial.filter((node) => node.metrics.goal.status === 'known').length /
                commercial.length,
              'Commercial goal coverage.',
            ),
          ]
        : [],
      'Explicit goals support commercial scoring.',
    ),
  ];
}

function retentionComponents(context: DiagnosisContext): ScoreComponent[] {
  const retention = context.graph.nodes.filter((node) => node.category === 'retention');
  const types = new Set(retention.map((node) => node.type));
  if (!retention.length) {
    return ['post_sale', 'renewal', 'upsell', 'churn'].map((id) =>
      component(
        'retention',
        id,
        null,
        [],
        'Retention applicability is unknown because no retention context is represented.',
      ),
    );
  }
  const rates = retention
    .map((node) => metricValue(node.metrics.conversion))
    .filter((value): value is number => value !== null);
  return [
    component(
      'retention',
      'post_sale',
      types.has('post-sale') ? 100 : 20,
      [evidence('represented', types.has('post-sale') ? 1 : 0, 'Post-sale stage representation.')],
      'Post-sale representation supports retention operations.',
    ),
    component(
      'retention',
      'renewal',
      types.has('renewal') ? 100 : 30,
      [evidence('represented', types.has('renewal') ? 1 : 0, 'Renewal stage representation.')],
      'Renewal representation is contextual evidence.',
    ),
    component(
      'retention',
      'upsell',
      types.has('upsell') ? 100 : 40,
      [evidence('represented', types.has('upsell') ? 1 : 0, 'Upsell stage representation.')],
      'Upsell representation is an optional retention signal.',
    ),
    component(
      'retention',
      'churn',
      rates.length ? (rates.reduce((sum, value) => sum + value, 0) / rates.length) * 100 : null,
      rates.length
        ? [
            evidence(
              'retention_conversion',
              rates.reduce((sum, value) => sum + value, 0) / rates.length,
              'Mean known retention-stage rate.',
            ),
          ]
        : [],
      'Known retention rates provide a quantitative signal.',
    ),
  ];
}

function validateConfig(config: ScoreConfig): string[] {
  const errors: string[] = [];
  if (!config.version.trim()) errors.push('Score configuration has no version.');
  if (!config.approvalId.trim()) errors.push('Score configuration has no approval identifier.');
  const dimensions = Object.values(config.dimensionWeights);
  if (Math.abs(dimensions.reduce((sum, value) => sum + value, 0) - 1) > 0.0001)
    errors.push('Dimension weights must sum to 1.');
  if (dimensions.some((value) => !Number.isFinite(value) || value < 0))
    errors.push('Dimension weights must be finite and non-negative.');
  for (const [dimension, weights] of Object.entries(config.componentWeights)) {
    const values = Object.values(weights);
    if (!values.length || Math.abs(values.reduce((sum, value) => sum + value, 0) - 1) > 0.0001)
      errors.push(`${dimension} component weights must sum to 1.`);
    if (values.some((value) => !Number.isFinite(value) || value < 0))
      errors.push(`${dimension} component weights must be finite and non-negative.`);
  }
  if (
    !Number.isFinite(config.minimumDimensionCoverage) ||
    config.minimumDimensionCoverage < 0 ||
    config.minimumDimensionCoverage > 1
  )
    errors.push('Dimension coverage threshold is invalid.');
  if (
    !Number.isFinite(config.minimumOverallCoverage) ||
    config.minimumOverallCoverage < 0 ||
    config.minimumOverallCoverage > 1
  )
    errors.push('Overall coverage threshold is invalid.');
  return errors;
}

function dimensionResult(
  dimension: ScoreDimension,
  components: ScoreComponent[],
  config: ScoreConfig | undefined,
): ScoreDimensionResult {
  const known = components.filter((item) => item.valueState === 'known');
  const coverage = components.length ? known.length / components.length : 0;
  const warnings: string[] = [];
  if (!config) warnings.push('Score weights and coverage thresholds require explicit approval.');
  const weights = config?.componentWeights[dimension];
  const available = known.filter((item) => weights?.[item.weightKey] !== undefined);
  const totalWeight = available.reduce((sum, item) => sum + (weights?.[item.weightKey] ?? 0), 0);
  const canScore = !!config && coverage >= config.minimumDimensionCoverage && totalWeight > 0;
  const score = canScore
    ? clamp(
        available.reduce(
          (sum, item) => sum + item.normalizedValue! * (weights![item.weightKey]! / totalWeight),
          0,
        ),
      )
    : undefined;
  if (config && coverage < config.minimumDimensionCoverage)
    warnings.push(
      'Insufficient dimensional coverage; missing inputs were excluded, not scored as zero.',
    );
  return {
    dimension,
    ...(score === undefined ? {} : { score }),
    coverage,
    confidence: Math.min(1, coverage * 0.8 + (known.length ? 0.2 : 0)),
    components,
    evidence: known.flatMap((item) => item.evidence),
    warnings,
    explanation:
      score === undefined
        ? 'Score remains unknown until approved configuration and sufficient evidence are available.'
        : `Known components were weighted and normalized to ${score.toFixed(1)} / 100; unknown components contributed neither value nor weight.`,
  };
}

export function calculateScores(context: DiagnosisContext): ScoreResult {
  const configErrors = context.scoreConfig ? validateConfig(context.scoreConfig) : [];
  const config = configErrors.length ? undefined : context.scoreConfig;
  const breakdown = [
    dimensionResult('acquisition', acquisitionComponents(context), config),
    dimensionResult('conversion', conversionComponents(context), config),
    dimensionResult('commercial', commercialComponents(context), config),
    dimensionResult('retention', retentionComponents(context), config),
  ];
  const known = breakdown.filter((item) => item.score !== undefined);
  const coverage = breakdown.reduce((sum, item) => sum + item.coverage, 0) / breakdown.length;
  const totalWeight = known.reduce(
    (sum, item) => sum + (config?.dimensionWeights[item.dimension] ?? 0),
    0,
  );
  const overall =
    config && coverage >= config.minimumOverallCoverage && totalWeight > 0
      ? clamp(
          known.reduce(
            (sum, item) =>
              sum + item.score! * (config.dimensionWeights[item.dimension] / totalWeight),
            0,
          ),
        )
      : undefined;
  const warnings = [
    ...configErrors,
    ...(!context.scoreConfig
      ? ['No approved score configuration is active; scores remain unknown.']
      : []),
    ...(context.scoreConfig && coverage < context.scoreConfig.minimumOverallCoverage
      ? ['Insufficient global score coverage.']
      : []),
  ];
  return {
    ...(overall === undefined ? {} : { overall }),
    acquisition: breakdown[0]!,
    conversion: breakdown[1]!,
    commercial: breakdown[2]!,
    retention: breakdown[3]!,
    coverage,
    confidence: known.length
      ? known.reduce((sum, item) => sum + item.confidence, 0) / known.length
      : 0,
    breakdown,
    warnings,
  };
}
